import { useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { OfferAPI } from '../../../api/payments/offer/api';
import { PlansAPI } from '../../../api/payments/plans/api';
import { ItemsAPI } from '../../../api/payments/items/api';
import { CouponsAPI } from '../../../api/payments/coupons/api';
import { buildItemsQueryParams, extractItems } from '../../Items/utils';
import {
  ruleActiveAtCycle,
  ruleMatchesItem,
  ruleMatchesPlan,
} from '../../Coupons/v2/utils';
import { ITEM_TYPE_ADDON, getItemPrice } from '../_components/OfferItemRow';
import { scrollToCenter } from '../../../helpers/motion';
import {
  dateInputValueToIso,
  toDateInputValue,
} from '../../../helpers/dateInput';

const SEARCH_DEBOUNCE_MS = 400;
const ITEMS_LIMIT = 100;

export function useItemSelectBootstrap() {
  const [searchText, setSearchText] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const requestId = useRef(0);

  // Busca no servidor por nome com debounce de 400ms.
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      const currentRequest = ++requestId.current;
      setLoading(true);
      try {
        const params = buildItemsQueryParams({
          page: 0,
          limit: ITEMS_LIMIT,
          filterText: searchText,
        });
        const response = await ItemsAPI.list(params);
        if (currentRequest === requestId.current) {
          setItems(extractItems(response));
        }
      } catch (error) {
        if (currentRequest === requestId.current) {
          setItems([]);
          toast.error(error?.message || 'Erro ao buscar itens.');
        }
      } finally {
        if (currentRequest === requestId.current) setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
  }, [searchText]);

  return { items, loading, setSearchText };
}

const DEFAULT_PAYMENT_METHOD = 'CREDIT_CARD';

const PAYMENT_METHOD_LABELS = {
  CREDIT_CARD: 'Cartão de crédito',
  PIX: 'Pix',
  BOLETO: 'Boleto',
};

// Usado caso a listagem de métodos de pagamento não possa ser carregada.
const FALLBACK_PAYMENT_METHODS = Object.keys(PAYMENT_METHOD_LABELS);

function normalizePaymentMethods(response) {
  const list = Array.isArray(response) ? response : response?.data ?? [];
  return list
    .map((method) => (typeof method === 'string' ? method : method?.id))
    .filter(Boolean)
    .map((id) => ({ id, label: PAYMENT_METHOD_LABELS[id] ?? id }));
}

// O e-mail pode vir de outra página via query string (?email=) ou location.state.
function getRedirectEmail(location) {
  const fromQuery = new URLSearchParams(location.search).get('email');
  return fromQuery || location.state?.email || '';
}

// Estimativa client-side de um ciclo de cobrança, o 1º por padrão (o valor
// final real de cada ciclo é calculado pelo backend). Regras com planId que não bate com o
// plano selecionado são ignoradas. Cupons agora miram items (ITEM/ITEM_TOTAL):
// ITEM com itemId desconta só as linhas daquele item; ITEM_TOTAL desconta a
// soma dos produtos; sem itemId, valem para todas as linhas.

function applyRule(amount, rule) {
  const value = Number(rule.value);
  if (!Number.isFinite(value)) return amount;
  const valueType = rule.valueType ?? rule.value_type;
  const discounted =
    valueType === 'PERCENT' ? amount * (1 - value / 100) : amount - value;
  return Math.max(0, discounted);
}

// Reduz proporcionalmente cada linha pra manter a soma das linhas igual ao
// valor da regra aplicada no agregado (exato pra PERCENT; alocação por
// participação pra FIXED, já que um desconto fixo sobre a soma não aponta
// pra uma linha específica).
function scaleRows(rowAmounts, rule) {
  const sumBefore = [...rowAmounts.values()].reduce((sum, v) => sum + v, 0);
  if (sumBefore <= 0) return;
  const sumAfter = applyRule(sumBefore, rule);
  const ratio = sumAfter / sumBefore;
  rowAmounts.forEach((amount, key) => rowAmounts.set(key, amount * ratio));
}

export function buildOfferSummary(selectedPlan, items, coupon, cycle = 1) {
  const planAmount = selectedPlan?.amount ?? 0;
  const rows = items
    .filter((item) => item.item)
    .map((item) => ({
      key: item.key,
      itemId: item.item.id,
      original: getItemPrice(item.item) * item.quantity,
    }));
  const itemsTotal = rows.reduce((sum, row) => sum + row.original, 0);
  const subtotal = planAmount + itemsTotal;

  const applicableRules = (coupon?.rules ?? []).filter(
    (rule) =>
      ruleActiveAtCycle(rule, cycle) && ruleMatchesPlan(rule, selectedPlan?.id),
  );

  let discountedPlan = planAmount;
  applicableRules
    .filter((rule) => rule.scope === 'PLAN')
    .forEach((rule) => {
      discountedPlan = applyRule(discountedPlan, rule);
    });

  // Desconto por produto (ITEM): só nas linhas do item da regra (sem itemId,
  // vale pra todas). "Total em produtos" (ITEM_TOTAL) desconta a soma, então
  // rateamos entre as linhas. Estimativa: o valor incide uma vez por linha.
  const rowAmounts = new Map(rows.map((row) => [row.key, row.original]));
  applicableRules
    .filter((rule) => rule.scope === 'ITEM')
    .forEach((rule) => {
      rows.forEach((row) => {
        if (!ruleMatchesItem(rule, row.itemId)) return;
        rowAmounts.set(row.key, applyRule(rowAmounts.get(row.key), rule));
      });
    });
  applicableRules
    .filter((rule) => rule.scope === 'ITEM_TOTAL')
    .forEach((rule) => scaleRows(rowAmounts, rule));

  let discountedItems = [...rowAmounts.values()].reduce((sum, v) => sum + v, 0);

  let finalTotal = discountedPlan + discountedItems;
  applicableRules
    .filter((rule) => rule.scope === 'ALL')
    .forEach((rule) => {
      // Desconto "ALL" incide sobre plano + add-ons juntos; rateia nas linhas
      // de add-on na mesma proporção pra manter a exibição por item coerente
      // com o total final mostrado no resumo.
      const before = finalTotal;
      finalTotal = applyRule(finalTotal, rule);
      if (before > 0) {
        const ratio = finalTotal / before;
        rowAmounts.forEach((amount, key) =>
          rowAmounts.set(key, amount * ratio),
        );
      }
    });
  discountedItems = [...rowAmounts.values()].reduce((sum, v) => sum + v, 0);

  const itemDiscounts = {};
  rows.forEach((row) => {
    const discounted = rowAmounts.get(row.key);
    if (discounted < row.original - 0.005) {
      itemDiscounts[row.key] = { original: row.original, discounted };
    }
  });

  return {
    subtotal,
    finalTotal,
    discountAmount: Math.max(0, subtotal - finalTotal),
    itemDiscounts,
  };
}

// O cupom dá desconto em algum ciclo? Olhar só o 1º dá falso alerta em promo
// "do ciclo 2 em diante". Vai até o ciclo seguinte ao maior limite das regras
// (a partir dele nada muda).
export function couponDiscountsAnyCycle(selectedPlan, items, coupon) {
  if (!coupon) return false;
  const last =
    Math.max(
      1,
      ...(coupon?.rules ?? []).flatMap((rule) => [
        Number(rule.fromCycle) || 1,
        Number(rule.toCycle) || 0,
      ]),
    ) + 1;
  return Array.from({ length: last }, (_, i) => i + 1).some(
    (cycle) =>
      buildOfferSummary(selectedPlan, items, coupon, cycle).discountAmount > 0,
  );
}

function mapAddonToItem(addon) {
  return {
    key: `addon-${addon.itemId}`,
    type: ITEM_TYPE_ADDON,
    itemId: addon.itemId,
    item: { ...addon, id: addon.itemId, name: addon.itemName },
    quantity: addon.quantity ?? 1,
  };
}

export function useCreateOfferBootstrap(
  offerId,
  initialOffer,
) {
  const history = useHistory();
  const location = useLocation();
  const redirectEmail = getRedirectEmail(location);

  const [offerFromState, setOfferFromState] = useState(
    initialOffer,
  );
  // true já no 1º render quando vai buscar: evita o flash do form vazio antes do skeleton.
  const [fetchingOffer, setFetchingOffer] = useState(
    !!offerId && !initialOffer,
  );
  const [notFound, setNotFound] = useState(false);

  const nextItemKey = useRef(0);

  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(true);

  useEffect(() => {
    PlansAPI.list()
      .then((response) => setPlans(response.data.content || []))
      .catch((error) => {
        setPlans([]);
        toast.error(error?.message || 'Erro ao carregar os planos.');
      })
      .finally(() => setPlansLoading(false));
    OfferAPI.listPaymentMethods()
      .then((response) => {
        const methods = normalizePaymentMethods(response);
        setPaymentMethods(
          methods.length
            ? methods
            : normalizePaymentMethods(FALLBACK_PAYMENT_METHODS),
        );
      })
      .catch(() =>
        setPaymentMethods(normalizePaymentMethods(FALLBACK_PAYMENT_METHODS)),
      )
      .finally(() => setPaymentMethodsLoading(false));
  }, []);

  useEffect(() => {
    if (!offerId || offerFromState) return;
    setFetchingOffer(true);
    OfferAPI.getV2(offerId)
      .then((response) => setOfferFromState(response.data ?? response))
      .catch(() => {
        toast.warn(
          'Não foi possível carregar os dados da oferta, tente novamente pela listagem.',
        );
        setNotFound(true);
      })
      .finally(() => setFetchingOffer(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId]);

  // Cupom pré-selecionado via ?couponId= (ex.: "Criar oferta" aberto em nova
  // aba, onde não há location.state). Só busca na criação e quando o cupom não
  // veio no location.state.
  const couponIdParam = new URLSearchParams(location.search).get('couponId');
  const couponFromState = location.state?.coupon ?? null;
  const shouldFetchCoupon = !offerId && !couponFromState && !!couponIdParam;
  const [couponFetch, setCouponFetch] = useState({ id: null, coupon: null });
  const couponResolved = couponFetch.id === couponIdParam;
  const fetchingCoupon = shouldFetchCoupon && !couponResolved;
  const couponFromQuery =
    shouldFetchCoupon && couponResolved ? couponFetch.coupon : null;

  useEffect(() => {
    if (!shouldFetchCoupon) return undefined;
    let cancelled = false;
    CouponsAPI.get(couponIdParam)
      .then((response) => {
        if (cancelled) return;
        const coupon = response.data ?? null;
        // CouponSelect só lista cupons ativos; não pré-seleciona um inativo.
        if (coupon && !coupon.active) {
          toast.warn(
            'O cupom informado está inativo e não pode ser usado em ofertas.',
          );
        }
        setCouponFetch({
          id: couponIdParam,
          coupon: coupon?.active ? coupon : null,
        });
      })
      // erro já exibido via toast pela função de api
      .catch(() => {
        if (!cancelled) setCouponFetch({ id: couponIdParam, coupon: null });
      });
    return () => {
      cancelled = true;
    };
  }, [shouldFetchCoupon, couponIdParam]);

  const customerEmail = offerFromState?.customerEmail ?? redirectEmail;

  const initialValues = {
    name: offerFromState?.name ?? '',
    planId: offerFromState?.planId ?? '',
    paymentMethods: offerFromState?.allowedPaymentMethods ?? [
      DEFAULT_PAYMENT_METHOD,
    ],
    expirationDate: toDateInputValue(offerFromState?.expirationDate),
    linkToCustomer: !!customerEmail,
    customerEmail,
    items: (offerFromState?.addons ?? []).map(mapAddonToItem),
    // location.state.coupon: vem do "Criar oferta" na listagem/detalhes do
    // cupom; sem ele, usa o cupom carregado de ?couponId=.
    coupon: offerFromState?.coupon ?? couponFromState ?? couponFromQuery,
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: yup.object().shape({
      name: yup
        .string()
        .required('O campo nome da oferta é obrigatório!')
        .matches(
          /^[A-Z0-9_-]+$/,
          'Use apenas letras maiúsculas, números, "_" ou "-".',
        ),
      planId: yup.string().required('Selecione o plano base!'),
      paymentMethods: yup
        .array()
        .of(yup.string())
        .min(1, 'Selecione ao menos um método de pagamento!'),
      expirationDate: yup.string().test(
        'not-past',
        'A data de expiração não pode ser anterior a hoje',
        // mantém o valor já salvo (oferta antiga expirada) sem bloquear
        // a edição de outros campos; datas 'yyyy-MM-dd' comparam como string.
        (value) =>
          !value ||
          value === initialValues.expirationDate ||
          value >= toDateInputValue(new Date()),
      ),
      items: yup.array().of(
        yup.object().shape({
          itemId: yup.string().required('Selecione o produto'),
        }),
      ),
      linkToCustomer: yup.boolean(),
      customerEmail: yup.string().when('linkToCustomer', {
        is: true,
        then: (schema) =>
          schema
            .email('Informe um e-mail válido')
            .required('O e-mail do cliente é obrigatório!'),
      }),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      try {
        const payload = {
          name: values.name,
          originFlow: 'url_subscription',
          planId: values.planId,
          allowedPaymentMethods: values.paymentMethods,
          expirationDate: dateInputValueToIso(values.expirationDate),
          customerEmail: values.linkToCustomer ? values.customerEmail : null,
          couponId: values.coupon ? values.coupon.id : null,
          addons: values.items.map((item) => ({
            itemId: item.itemId,
            quantity: item.quantity,
          })),
        };
        const saved = offerId
          ? await OfferAPI.updateV2(offerId, payload)
          : await OfferAPI.createV2(payload);
        toast.success('Oferta salva com sucesso!');
        const id = offerId ?? saved.id;
        // sem id (backend antigo sem o campo): cai na listagem
        history.push(id ? `/payments/offers/${id}` : '/payments/offers');
      } catch (error) {
        // 400 do backend vem em response.data.message (ex.: "Cliente não
        // encontrado..."); error.message do axios seria só o status HTTP.
        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            'Ocorreu um erro ao criar o link de pagamento.',
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleLinkToCustomerChange = (event) =>
    formik.setFieldValue('linkToCustomer', event.target.checked);

  const [extraPlan, setExtraPlan] = useState(null);
  useEffect(() => {
    const planId = formik.values.planId;
    if (!planId || plans.some((plan) => plan.id === planId)) {
      setExtraPlan(null);
      return;
    }
    PlansAPI.get(planId)
      .then((response) => setExtraPlan(response.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.planId, plans]);

  const selectedPlan =
    plans.find((plan) => plan.id === formik.values.planId) ??
    (extraPlan?.id === formik.values.planId ? extraPlan : null);
  const planItems = selectedPlan?.planItems ?? [];

  const offerSummary = buildOfferSummary(
    selectedPlan,
    formik.values.items,
    formik.values.coupon,
  );

  const couponHasDiscount = couponDiscountsAnyCycle(
    selectedPlan,
    formik.values.items,
    formik.values.coupon,
  );

  const getCycleTotal = (cycle) =>
    buildOfferSummary(
      selectedPlan,
      formik.values.items,
      formik.values.coupon,
      cycle,
    ).finalTotal;

  // Linha nova: rola e foca só depois da animação de entrada (antes disso a linha
  // ainda não tem altura e o scroll erra o centro).
  const pendingItemKey = useRef(null);
  const handleItemEntered = (item) => {
    if (pendingItemKey.current !== item.key) return;
    pendingItemKey.current = null;
    const row = document.getElementById(`offer-item-${item.key}`);
    scrollToCenter(row);
    row?.querySelector('input')?.focus({ preventScroll: true });
  };

  const handleAddItem = () => {
    const key = nextItemKey.current++;
    pendingItemKey.current = key;
    formik.setFieldValue('items', [
      ...formik.values.items,
      {
        key,
        type: ITEM_TYPE_ADDON,
        itemId: '',
        item: null,
        quantity: 1,
        // UI-only: linha criada pelo usuário entra com destaque (jds-flash).
        _flash: true,
      },
    ]);
  };

  const handleItemChange = (key, changes) =>
    formik.setFieldValue(
      'items',
      formik.values.items.map((item) =>
        item.key === key ? { ...item, ...changes } : item,
      ),
    );

  const handleRemoveItem = (key) =>
    formik.setFieldValue(
      'items',
      formik.values.items.filter((item) => item.key !== key),
    );

  const handleCancel = () => {
    if (
      formik.dirty &&
      !window.confirm('Há alterações não salvas. Deseja descartá-las?')
    ) {
      return;
    }
    history.push('/payments/offers');
  };

  return {
    notFound,
    // Evita editar o form enquanto o cupom carrega (enableReinitialize
    // resetaria as edições quando o cupom chegasse).
    fetchingOffer: fetchingOffer || fetchingCoupon,
    formik,
    plans,
    plansLoading,
    planItems,
    selectedPlan,
    offerSummary,
    couponHasDiscount,
    getCycleTotal,
    handleAddItem,
    handleItemEntered,
    handleItemChange,
    handleRemoveItem,
    paymentMethods,
    paymentMethodsLoading,
    handleLinkToCustomerChange,
    handleCancel,
  };
}
